import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { roundToTwoDecimalPlaces, calculateRepayment, useGenerateId, createInputHandler, Chart, DEFAULT_FORMAT } from '@lendi-jam/atoms';
import Grid from '@lendi-ui/grid';
import Error from '@lendi-ui/icon/Error';
import Help from '@lendi-ui/icon/Help';
import { Input } from '@lendi-ui/text-input';
import { Heading, Body } from '@lendi-ui/typography';
import numeral from 'numeral';
import { useForm } from 'react-hook-form';
import { Tooltip } from 'recharts';
import * as yup from 'yup';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { bg, color } from '@lendi-ui/color';
import { p, mr, my, mb, py, mx, px, pr, mt } from '@lendi-ui/spacing';
import styled from 'styled-components';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { gte, between } from '@lendi-ui/breakpoint';
import { Button } from '@lendi-ui/button';
import Container from '@lendi-ui/container';
import LendiTooltip from '@lendi-ui/tooltip';
import { embedMarketingTrackingIntoURL } from '@lendi-jam/jam-link';

var Colors = /*#__PURE__*/function (Colors) {
  Colors["WithoutOffset"] = "#00c0a5";
  Colors["WithOffset"] = "#006c8a";
  return Colors;
}({});
var calculateOffsetBalance = function calculateOffsetBalance(initialOffset, offsetContribution, period) {
  return initialOffset + offsetContribution * period;
};
var calculateMonthlyInterest = function calculateMonthlyInterest(_ref) {
  var currentMonthsBalance = _ref.currentMonthsBalance,
    monthlyInterestRate = _ref.monthlyInterestRate,
    _ref$offsetBalance = _ref.offsetBalance,
    offsetBalance = _ref$offsetBalance === void 0 ? 0 : _ref$offsetBalance;
  return offsetBalance >= currentMonthsBalance ? 0 : (currentMonthsBalance - offsetBalance) * monthlyInterestRate;
};
var principleRemaining = function principleRemaining(_ref2) {
  var interestRate = _ref2.interestRate,
    loanAmount = _ref2.loanAmount,
    loanTerm = _ref2.loanTerm,
    _ref2$offsetStartDela = _ref2.offsetStartDelay,
    offsetStartDelay = _ref2$offsetStartDela === void 0 ? 0 : _ref2$offsetStartDela,
    _ref2$monthlyOffsetIn = _ref2.monthlyOffsetIncrement,
    monthlyOffsetIncrement = _ref2$monthlyOffsetIn === void 0 ? 0 : _ref2$monthlyOffsetIn,
    _ref2$initialOffsetBa = _ref2.initialOffsetBalance,
    initialOffsetBalance = _ref2$initialOffsetBa === void 0 ? 0 : _ref2$initialOffsetBa;
  if (loanTerm <= 0) {
    return {
      accumulatedInterest: 0,
      result: [],
      revisedLoanTerm: 0
    };
  }
  var accumulatedInterest = 0;
  var newLoanTerm = 0;
  var result = [loanAmount];
  var n = loanTerm * 12;
  var monthlyInterestRate = interestRate / 12 / 100;

  // TODO: consider making this cents,
  // Make a "$" formatter, instead of Math.round
  var monthlyRepayment = roundToTwoDecimalPlaces(calculateRepayment({
    loanAmount: loanAmount,
    interestRate: interestRate,
    loanTerm: loanTerm,
    repaymentFrequency: 12
  }));
  var currentMonthsBalance = loanAmount;
  var balanceForNextMonth = function balanceForNextMonth(monthlyInterest, currentBalance) {
    return currentBalance + monthlyInterest - monthlyRepayment;
  };
  for (var i = 1; i < n; i++) {
    var hasOffsetStarted = i > offsetStartDelay * 12;
    var offsetPeriod = i; // TODO: Confirm this with Emma B, do we calculate from beginnning of the term or end of delay
    var offsetBalance = hasOffsetStarted ? calculateOffsetBalance(initialOffsetBalance, monthlyOffsetIncrement, offsetPeriod) : 0;
    var monthlyInterest = roundToTwoDecimalPlaces(calculateMonthlyInterest({
      currentMonthsBalance: currentMonthsBalance,
      monthlyInterestRate: monthlyInterestRate,
      offsetBalance: offsetBalance
    }));
    accumulatedInterest += monthlyInterest;
    var futureBalance = roundToTwoDecimalPlaces(balanceForNextMonth(monthlyInterest, currentMonthsBalance));
    currentMonthsBalance = futureBalance;
    if (futureBalance <= 0) {
      currentMonthsBalance = 0;
      if (newLoanTerm === 0) newLoanTerm = i / 12;
    }
    if (i % 12 === 0 && futureBalance > 0) {
      result.push(Math.round(futureBalance));
    }
  }
  if (result[result.length - 1] !== 0) {
    result.push(0);
  }
  return {
    accumulatedInterest: accumulatedInterest,
    result: result,
    revisedLoanTerm: newLoanTerm
  };
};

var _templateObject$2, _templateObject2$2, _templateObject3$2;
var TooltipWrapper = styled.div(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteral(["\n  align-items: center;\n  border-radius: 5px;\n  box-shadow: 1px 2px 6px -3px black;\n  box-sizing: border-box;\n  display: flex;\n  flex-wrap: wrap;\n  row-gap: 8px;\n  width: 240px;\n  ", "\n  ", "\n\n  ", " {\n    text-align: right;\n  }\n\n  strong {\n    font-size: 12px;\n  }\n"])), bg('shade.0'), p('sm'), "".concat(Grid.Unit, ":nth-child(odd)"));
var Row = styled.div(_templateObject2$2 || (_templateObject2$2 = _taggedTemplateLiteral(["\n  flex-basis: 100%;\n"])));
var ColorKey = styled.div(_templateObject3$2 || (_templateObject3$2 = _taggedTemplateLiteral(["\n  width: 12px;\n  display: inline-block;\n  height: 12px;\n  background-color: ", ";\n  ", "\n"])), function (_ref) {
  var color = _ref.color;
  return color;
}, mr('xxxs'));

var GraphTooltip = function GraphTooltip(_ref) {
  var _payload$, _numeral$format;
  var _ref$payload = _ref.payload,
    payload = _ref$payload === void 0 ? [] : _ref$payload;
  var currentPayload = (_payload$ = payload[0]) === null || _payload$ === void 0 ? void 0 : _payload$.payload;
  if (!currentPayload) return null;
  var year = Math.floor(currentPayload.years);
  var month = Math.round(currentPayload.years % 1 * 12);
  return /*#__PURE__*/jsxs(TooltipWrapper, {
    children: [/*#__PURE__*/jsxs(Row, {
      children: [year ? /*#__PURE__*/jsxs(Fragment, {
        children: ["Year: ", year]
      }) : null, year && month ? ' | ' : '', month ? /*#__PURE__*/jsxs(Fragment, {
        children: ["Month: ", month]
      }) : null]
    }), /*#__PURE__*/jsxs(Grid.Unit, {
      size: 8 / 12,
      children: [/*#__PURE__*/jsx(ColorKey, {
        color: Colors.WithoutOffset
      }), /*#__PURE__*/jsx("strong", {
        children: "Without offset"
      })]
    }), /*#__PURE__*/jsxs(Grid.Unit, {
      size: 4 / 12,
      children: ["$", numeral(currentPayload.withoutOffset).format('0,0')]
    }), /*#__PURE__*/jsxs(Grid.Unit, {
      size: 8 / 12,
      children: [/*#__PURE__*/jsx(ColorKey, {
        color: Colors.WithOffset
      }), /*#__PURE__*/jsx("strong", {
        children: "With offset"
      })]
    }), /*#__PURE__*/jsxs(Grid.Unit, {
      size: 4 / 12,
      children: ["$", (_numeral$format = numeral(currentPayload.withOffset).format('0,0')) !== null && _numeral$format !== void 0 ? _numeral$format : 0]
    })]
  });
};

var _templateObject$1, _templateObject2$1, _templateObject3$1, _templateObject4$1, _templateObject5$1, _templateObject6$1, _templateObject7$1, _templateObject8$1, _templateObject9$1, _templateObject10$1, _templateObject11$1, _templateObject12$1, _templateObject13$1, _templateObject14$1, _templateObject15$1, _templateObject16$1, _templateObject17$1, _templateObject18$1;
var Wrapper$1 = styled(Container)(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n  ", "\n"])), my('md'));
var Calculator = styled.form(_templateObject2$1 || (_templateObject2$1 = _taggedTemplateLiteral(["\n  background-color: #f8f8f8;\n  border-radius: 5px;\n  ", "\n"])), p({
  lg: 'lg',
  xs: 'md'
}));
var Title = styled(Heading)(_templateObject3$1 || (_templateObject3$1 = _taggedTemplateLiteral(["\n  ", "\n"])), mb('md'));
var StyledHeading$1 = styled(Heading)(_templateObject4$1 || (_templateObject4$1 = _taggedTemplateLiteral(["\n  display: block;\n\n  small {\n    font-size: 18px;\n  }\n"])));
var GraphHeading = styled(StyledHeading$1)(_templateObject5$1 || (_templateObject5$1 = _taggedTemplateLiteral(["\n  ", "\n"])), py('sm'));
var StyledUnit = styled(Grid.Unit)(_templateObject6$1 || (_templateObject6$1 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: column;\n  ", "\n  row-gap: 6px;\n\n  &:last-child {\n    ", "\n  }\n\n  & > div {\n    width: 100%;\n  }\n"])), mb('md'), mb('nil'));
var StyledButton = styled(Button)(_templateObject7$1 || (_templateObject7$1 = _taggedTemplateLiteral(["\n  width: 100%;\n\n  ", "\n  ", "\n"])), gte('lg')(_templateObject8$1 || (_templateObject8$1 = _taggedTemplateLiteral(["\n    max-width: 36%;\n  "]))), mx('auto'));
var InputAfter = styled(Body)(_templateObject9$1 || (_templateObject9$1 = _taggedTemplateLiteral(["\n  font-weight: 100;\n  color: #777777;\n  ", "\n"])), px('xxs'));
var InputBefore = styled(InputAfter)(_templateObject10$1 || (_templateObject10$1 = _taggedTemplateLiteral(["\n  ", "\n"])), pr('xxxs'));
var ResultsWrapper = styled.div(_templateObject11$1 || (_templateObject11$1 = _taggedTemplateLiteral(["\n  ", "\n  font-family: 'Open Sans', sans-serif;\n  font-size: 14px;\n  border: 1px solid #e3e3e3;\n  border-bottom-left-radius: 5px;\n  border-bottom-left-radius: 5px;\n  box-sizing: border-box;\n"])), p({
  lg: 'lg',
  xs: 'md'
}));
var Summary = styled(Body)(_templateObject12$1 || (_templateObject12$1 = _taggedTemplateLiteral(["\n  ", "\n"])), my('xxs'));
var PaymentSummary = styled.div(_templateObject13$1 || (_templateObject13$1 = _taggedTemplateLiteral(["\n  border-radius: 5px;\n  box-sizing: border-box;\n  color: #464646;\n  font-size: 12px;\n  width: 100%;\n  ", "\n  ", "\n  ", "\n  ", "\n  ", "\n\n  ", "\n  ", ";\n\n  ", " {\n    row-gap: 15px;\n  }\n"])), bg('shade.25'), mb('xxxl'), mt('xxl'), mx('auto'), p('md'), gte('sm')(_templateObject14$1 || (_templateObject14$1 = _taggedTemplateLiteral(["\n    font-size: 16px;\n  "]))), gte('md')(_templateObject15$1 || (_templateObject15$1 = _taggedTemplateLiteral(["\n    width: 60%;\n  "]))), Grid);
var Seperator$1 = styled.div(_templateObject16$1 || (_templateObject16$1 = _taggedTemplateLiteral(["\n  width: 100%;\n  background-color: ", ";\n  height: 1px;\n  flex-basis: 100%;\n"])), color('shade.50'));
var RightAlignUnit = styled(Grid.Unit)(_templateObject17$1 || (_templateObject17$1 = _taggedTemplateLiteral(["\n  text-align: right;\n"])));
var StyledLendiTooltip = styled(LendiTooltip)(_templateObject18$1 || (_templateObject18$1 = _taggedTemplateLiteral(["\n  p {\n    margin: 0;\n  }\n"])));

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17, _templateObject18, _templateObject19, _templateObject20, _templateObject21, _templateObject22, _templateObject23, _templateObject24;
styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  ", "\n\n  ", "\n"])), mx('auto'), between('xs', 'sm')(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    text-align: center;\n  "]))));
var StyledHeading = styled(Heading)(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  ", "\n\n  strong {\n    margin-right: 4px;\n  }\n\n  small {\n    display: inline-block;\n    font-weight: 600;\n    font-size: 22px;\n  }\n"])), mt({
  xl: 'lg',
  xs: 'md'
}));
var Wrapper = styled.div(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  background-color: #003046;\n  width: 100%;\n  box-sizing: border-box;\n  color: white;\n  z-index: 1;\n  border-top-left-radius: 5px;\n  border-top-right-radius: 5px;\n  border: none;\n  ", "\n  ", "\n\n  ", " {\n    height: 100%;\n  }\n\n  a {\n    align-self: flex-end;\n  }\n"])), p({
  lg: 'lg',
  xs: 'md'
}), mt('md'), Grid);
var StyledGrid = styled(Grid)(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  position: relative;\n"])));
var ResultBody = styled(Body)(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n  ", "\n  ", "\n  font-size: 14px;\n\n  ", "\n"])), mt('sm'), mb('xxxs'), between('md', 'lg')(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    max-width: 85%;\n  "]))));
var SeperatorContainer = styled(Grid.Unit)(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n  ", "\n"])), function (_ref) {
  var interestOnly = _ref.interestOnly;
  return gte(interestOnly ? 'lg' : 'md')(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    padding: 0;\n  "])));
});
var Seperator = styled.div(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n  background-color: white;\n  box-sizing: border-box;\n  height: 1px;\n  margin: 28px auto;\n  padding: 0;\n  width: 100%;\n\n  ", "\n\n  ", "\n"])), function (_ref2) {
  var interestOnly = _ref2.interestOnly;
  return gte(interestOnly ? 'lg' : 'md')(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    width: unset;\n    margin: 0;\n    height: unset;\n  "])));
}, function (_ref3) {
  var interestOnly = _ref3.interestOnly;
  return gte(interestOnly ? 'lg' : 'md')(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    margin-left: -1px;\n    width: 1px;\n    min-height: 100%;\n  "])));
});
var Segment = styled.div(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n  width: 100%;\n\n  ", "\n\n  ", "\n\n  ", "\n\n  &:last-child {\n    ", "\n    display: flex;\n  }\n"])), gte('sm')(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    width: 50%;\n  "]))), function (_ref4) {
  var interestOnly = _ref4.interestOnly;
  return !interestOnly && gte('md')(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n      width: unset;\n    "])));
}, gte('lg')(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    width: 100%;\n  "]))), mt({
  xs: 'sm',
  sm: 'nil'
}));
var ResultDisplay = styled.div(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: space-between;\n  flex-wrap: wrap;\n\n  ", "\n\n  & > div {\n    width: 100%;\n\n    ", "\n\n    ", "\n\n    &:last-child {\n      ", "\n    }\n  }\n\n  ", " {\n    ", "\n  }\n"])), gte('md')(_templateObject18 || (_templateObject18 = _taggedTemplateLiteral(["\n    flex-wrap: no-wrap;\n  "]))), gte('sm')(_templateObject19 || (_templateObject19 = _taggedTemplateLiteral(["\n      width: 50%;\n    "]))), gte('lg')(_templateObject20 || (_templateObject20 = _taggedTemplateLiteral(["\n      width: unset;\n    "]))), mx({
  xs: 'nil',
  lg: 'auto'
}), Body, mt('xxxs'));
var SmallerHeading = styled.span(_templateObject21 || (_templateObject21 = _taggedTemplateLiteral(["\n  font-size: 22px;\n"])));
var NextSteps = styled(Grid.Unit)(_templateObject22 || (_templateObject22 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-wrap: wrap;\n\n  ", "\n\n  ", "\n  \n  ", " {\n    flex-basis: 100%;\n  }\n"])), between('xs', 'lg')(_templateObject23 || (_templateObject23 = _taggedTemplateLiteral(["\n    justify-content: space-between;\n  "]))), gte('lg')(_templateObject24 || (_templateObject24 = _taggedTemplateLiteral(["\n    row-gap: 20px;\n    justify-content: center;\n  "]))), Heading);

var TimeSavings = function TimeSavings(_ref) {
  var years = _ref.years;
  var numYears = Math.floor(years);
  var months = Math.round(years % 1 * 12);
  return numYears || months ? /*#__PURE__*/jsxs(Fragment, {
    children: [numYears ? /*#__PURE__*/jsxs(Fragment, {
      children: [numYears, " ", /*#__PURE__*/jsxs("small", {
        children: ["years", months ? ',' : '']
      })]
    }) : null, months ? /*#__PURE__*/jsxs(Fragment, {
      children: [numYears ? ' ' : '', months, " ", /*#__PURE__*/jsx("small", {
        children: "months"
      })]
    }) : null]
  }) : /*#__PURE__*/jsx(SmallerHeading, {
    children: "Less than a month"
  });
};
var ResultsPanel = function ResultsPanel(_ref2) {
  var buttonLink = _ref2.buttonLink,
    buttonText = _ref2.buttonText,
    interestSaved = _ref2.interestSaved,
    resultBody = _ref2.resultBody,
    resultHeading = _ref2.resultHeading,
    yearsSaved = _ref2.yearsSaved;
  return /*#__PURE__*/jsx(Wrapper, {
    "data-testid": "results-panel-wrapper",
    children: /*#__PURE__*/jsxs(StyledGrid, {
      gutter: "xxxl",
      children: [/*#__PURE__*/jsxs(Grid.Unit, {
        size: {
          lg: 2 / 3,
          md: 1
        },
        children: [/*#__PURE__*/jsx(Heading, {
          size: "sm",
          children: "With an offset, you could save:"
        }), /*#__PURE__*/jsxs(ResultDisplay, {
          children: [/*#__PURE__*/jsxs("div", {
            children: [/*#__PURE__*/jsx(StyledHeading, {
              size: "lg",
              children: /*#__PURE__*/jsxs("strong", {
                "data-testid": "interest-saved-val",
                children: ["$", interestSaved === null || interestSaved === void 0 ? void 0 : interestSaved.toLocaleString()]
              })
            }), /*#__PURE__*/jsx(Body, {
              size: "sm",
              children: "Interest savings over loan term"
            })]
          }), /*#__PURE__*/jsxs("div", {
            children: [/*#__PURE__*/jsx(StyledHeading, {
              size: "lg",
              children: /*#__PURE__*/jsx("strong", {
                children: /*#__PURE__*/jsx(TimeSavings, {
                  years: yearsSaved || 0
                })
              })
            }), /*#__PURE__*/jsx(Body, {
              size: "sm",
              children: "Time savings over loan term "
            })]
          })]
        })]
      }), /*#__PURE__*/jsx(SeperatorContainer, {
        interestOnly: true,
        size: {
          lg: 0,
          md: 1
        } ,
        children: /*#__PURE__*/jsx(Seperator, {
          interestOnly: true
        })
      }), /*#__PURE__*/jsx(Grid.Unit, {
        size: {
          lg: 1 / 3,
          md: 1
        } ,
        children: /*#__PURE__*/jsx(Grid, {
          halign: "justify-center",
          children: /*#__PURE__*/jsxs(NextSteps, {
            size: 1 ,
            children: [/*#__PURE__*/jsxs(Segment, {
              interestOnly: true,
              children: [/*#__PURE__*/jsx(Heading, {
                size: "sm",
                children: resultHeading
              }), /*#__PURE__*/jsx(ResultBody, {
                size: "xs",
                children: resultBody
              })]
            }), /*#__PURE__*/jsx(Segment, {
              interestOnly: true,
              children: buttonText && buttonLink && /*#__PURE__*/jsx(Button, {
                isFullWidth: true,
                type: "submit",
                variant: "primary",
                size: "md",
                href: embedMarketingTrackingIntoURL(buttonLink),
                children: buttonText
              })
            })]
          })
        })
      })]
    })
  });
};

var DEFAULT_VALUES = {
  initialOffsetBalance: '10,000',
  interestRate: '6.09',
  loanAmount: '450,000',
  loanTerm: 30,
  monthlyOffsetIncrement: '0',
  offsetStartDelay: 0
};
var schema = yup.object().shape({
  loanAmount: yup.mixed().transform(function (string) {
    return numeral(string).value();
  }).test({
    name: 'min',
    message: 'Loan amount must be greater than $10,000',
    test: function test(loanAmount) {
      return loanAmount >= 10000;
    }
  }).test({
    name: 'max',
    message: 'Loan amount must be equal to or less than $100 million',
    test: function test(loanAmount) {
      return loanAmount <= 100000000;
    }
  }).required(),
  interestRate: yup.number().typeError('Interest rate must be greater than 0%').min(0.01, 'Interest rate must be greater than 0%').max(20, 'Interest rate must be equal to or less than 20%').required('Interest rate must be greater than 0%'),
  loanTerm: yup.number().typeError('Loan term must be greater than 0 years').min(1, 'Loan term must be greater than 0 years').max(30, 'Loan term must be equal to or less than 30 years').integer('Please enter a whole number').required('Loan term must be greater than 0 years'),
  initialOffsetBalance: yup.mixed().transform(function (string) {
    return numeral(string).value();
  }).test({
    name: 'min',
    message: 'Offset Balance must be greater than $1,000',
    test: function test(initialOffsetBalance) {
      return initialOffsetBalance >= 1000;
    }
  }).test({
    name: 'lessThan',
    message: 'Offset Balance must be less than loan amount',
    test: function test(initialOffsetBalance) {
      var otherValue = this.resolve(yup.ref('loanAmount'));
      return initialOffsetBalance <= otherValue;
    }
  }).test({
    name: 'max',
    message: 'Offset Balance must be equal to or less than $100 million',
    test: function test(initialOffsetBalance) {
      return initialOffsetBalance <= 100000000;
    }
  }).required('Offset Balance must be greater than $1,000'),
  monthlyOffsetIncrement: yup.mixed().transform(function (string) {
    return numeral(string).value();
  }).test({
    name: 'min',
    message: 'Contributions must be greater than $0',
    test: function test(contributions) {
      return contributions >= 0;
    }
  }).test({
    name: 'lessThan',
    message: 'Contributions must be less than loan amount',
    test: function test(contributions) {
      var otherValue = this.resolve(yup.ref('loanAmount'));
      return contributions <= otherValue;
    }
  }).test({
    name: 'max',
    message: 'Contributions must be equal to or less than $100 million',
    test: function test(contributions) {
      return contributions <= 100000000;
    }
  }).required('Please enter a number'),
  offsetStartDelay: yup.number().typeError('Please enter a whole number').lessThan(yup.ref('loanTerm'), 'Offset must start prior to loan term ending').integer('Please enter a whole number').required('Please enter a whole number')
});
var parseTime = function parseTime(years) {
  var months = Math.round(years % 1 * 12) % 12;
  var numYears = months === 0 ? Math.round(years) : Math.floor(years);
  return "".concat(numYears ? "".concat(numYears, " years").concat(months ? ', ' : '') : '').concat(months ? "".concat(months, " months") : '');
};
var RepaymentsCalculator = function RepaymentsCalculator(_ref) {
  var buttonText = _ref.buttonText,
    buttonLink = _ref.buttonLink,
    calculatorHeading = _ref.calculatorHeading,
    disclaimer = _ref.disclaimer,
    graphHeading = _ref.graphHeading,
    contributionsTooltipContent = _ref.contributionsTooltipContent,
    offsetDelayTooltipContent = _ref.offsetDelayTooltipContent,
    resultBody = _ref.resultBody,
    resultHeading = _ref.resultHeading,
    showGraph = _ref.showGraph;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    chartData = _useState2[0],
    setChartData = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    results = _useState4[0],
    setResults = _useState4[1];
  var generateId = useGenerateId();
  var _useForm = useForm({
      resolver: yupResolver(schema),
      reValidateMode: 'onChange',
      mode: 'onChange',
      defaultValues: DEFAULT_VALUES
    }),
    errors = _useForm.errors,
    handleSubmit = _useForm.handleSubmit,
    register = _useForm.register,
    reset = _useForm.reset,
    trigger = _useForm.trigger,
    _useForm$formState = _useForm.formState,
    isValid = _useForm$formState.isValid,
    isDirty = _useForm$formState.isDirty,
    isSubmitted = _useForm$formState.isSubmitted;
  var loanAmountHandler = createInputHandler(100000000, function () {
    return trigger(['loanAmount', 'monthlyOffsetIncrement', 'initialOffsetBalance']);
  });
  var loanTermHandler = createInputHandler(30, function () {
    return trigger(['loanTerm', 'offsetStartDelay']);
  });
  var interestRateHandler = createInputHandler(20, function () {
    return trigger('interestRate');
  }, '0.00');
  var initialOffsetBalanceHandler = createInputHandler(100000000, function () {
    return trigger('initialOffsetBalance');
  });
  var monthlyOffsetIncrementHandler = createInputHandler(100000000, function () {
    return trigger('monthlyOffsetIncrement');
  });
  var offsetStartDelayHandler = createInputHandler(30, function () {
    return trigger('offsetStartDelay');
  });
  var onSubmit = function onSubmit(_ref2) {
    var loanAmount = _ref2.loanAmount,
      interestRate = _ref2.interestRate,
      loanTerm = _ref2.loanTerm,
      initialOffsetBalance = _ref2.initialOffsetBalance,
      monthlyOffsetIncrement = _ref2.monthlyOffsetIncrement,
      offsetStartDelay = _ref2.offsetStartDelay;
    // Home loan - no extra contributions or offset.
    var originalPrincipleRemaining = principleRemaining({
      loanTerm: loanTerm,
      loanAmount: loanAmount,
      interestRate: interestRate
    });
    var offsetPrincipleRemaining = principleRemaining({
      loanTerm: loanTerm,
      loanAmount: loanAmount,
      interestRate: interestRate,
      monthlyOffsetIncrement: monthlyOffsetIncrement,
      offsetStartDelay: offsetStartDelay,
      initialOffsetBalance: initialOffsetBalance
    });
    var newLoanTerm = offsetPrincipleRemaining.revisedLoanTerm;
    var yearsSaved = loanTerm - newLoanTerm;
    var interestSaved = Math.round(originalPrincipleRemaining.accumulatedInterest - offsetPrincipleRemaining.accumulatedInterest);
    var combinedPrincipleRemaining = originalPrincipleRemaining.result.flatMap(function (originalPrinciple, index) {
      var offsetPrinciple = offsetPrincipleRemaining.result[index];
      var data = {
        withoutOffset: originalPrinciple,
        years: index
      };
      if (index > newLoanTerm && index - 1 < newLoanTerm) {
        var yearLeft = Math.round(yearsSaved % 1 * 100) / 100;

        // difference is more than 1 month
        if (yearLeft * 12 > 1) {
          return [{
            years: index - 1 + (1 - yearLeft),
            withoutOffset: Math.round(originalPrinciple + (originalPrincipleRemaining.result[index - 1] - originalPrinciple) * yearLeft),
            withOffset: 0
          }, data];
        }
      }
      if (offsetPrinciple !== undefined) {
        data.withOffset = offsetPrinciple;
      }
      return data;
    });
    reset(_objectSpread(_objectSpread({}, DEFAULT_VALUES), {}, {
      loanAmount: numeral(loanAmount).format(DEFAULT_FORMAT),
      interestRate: numeral(interestRate).format('0.00'),
      loanTerm: loanTerm,
      initialOffsetBalance: numeral(initialOffsetBalance).format(DEFAULT_FORMAT),
      monthlyOffsetIncrement: numeral(monthlyOffsetIncrement).format(DEFAULT_FORMAT),
      offsetStartDelay: offsetStartDelay
    }));
    setChartData(combinedPrincipleRemaining);
    setResults({
      interestSaved: interestSaved,
      yearsSaved: yearsSaved,
      loanAmount: loanAmount,
      loanTerm: loanTerm,
      newLoanTerm: newLoanTerm
    });
  };
  return /*#__PURE__*/jsxs(Wrapper$1, {
    children: [/*#__PURE__*/jsxs(Calculator, {
      onSubmit: handleSubmit(onSubmit),
      children: [calculatorHeading && /*#__PURE__*/jsx(Title, {
        size: 'sm',
        children: calculatorHeading
      }), /*#__PURE__*/jsxs(Grid, {
        children: [/*#__PURE__*/jsxs(StyledUnit, {
          size: {
            lg: 1 / 4,
            sm: 1 / 2,
            xs: 1
          },
          children: [/*#__PURE__*/jsx("label", {
            htmlFor: generateId('current-loan-amount'),
            children: /*#__PURE__*/jsx(StyledHeading$1, {
              size: "xxs",
              children: "Current loan amount"
            })
          }), /*#__PURE__*/jsx(Input, {
            ref: register,
            name: "loanAmount",
            isFullWidth: true,
            size: "md",
            type: "text",
            id: generateId('current-loan-amount'),
            onChange: function onChange() {
              return undefined;
            },
            onFocus: loanAmountHandler.focus,
            onBlur: loanAmountHandler.blur,
            before: /*#__PURE__*/jsx(InputBefore, {
              children: "$"
            })
          }), errors.loanAmount && /*#__PURE__*/jsxs(Body, {
            color: "error.500",
            size: "xs",
            children: [/*#__PURE__*/jsx(Error, {
              color: "error.500"
            }), " ", errors.loanAmount.message]
          })]
        }), /*#__PURE__*/jsxs(StyledUnit, {
          size: {
            lg: 1 / 4,
            xs: 1 / 2
          },
          children: [/*#__PURE__*/jsx("label", {
            htmlFor: generateId('current-interest-rate'),
            children: /*#__PURE__*/jsx(StyledHeading$1, {
              size: "xxs",
              children: "Interest rate"
            })
          }), /*#__PURE__*/jsx(Input, {
            ref: register,
            name: "interestRate",
            isFullWidth: true,
            size: "md",
            type: "text",
            id: generateId('current-interest-rate'),
            onChange: function onChange() {
              return undefined;
            },
            onFocus: interestRateHandler.focus,
            onBlur: interestRateHandler.blur,
            after: /*#__PURE__*/jsx(InputAfter, {
              children: "%"
            })
          }), errors.interestRate && /*#__PURE__*/jsxs(Body, {
            color: "error.500",
            size: "xs",
            children: [/*#__PURE__*/jsx(Error, {
              color: "error.500"
            }), " ", errors.interestRate.message]
          })]
        }), /*#__PURE__*/jsxs(StyledUnit, {
          size: {
            lg: 1 / 4,
            xs: 1 / 2
          },
          children: [/*#__PURE__*/jsx("label", {
            htmlFor: generateId('current-loan-term'),
            children: /*#__PURE__*/jsx(StyledHeading$1, {
              size: "xxs",
              children: "Loan term"
            })
          }), /*#__PURE__*/jsx(Input, {
            ref: register,
            name: "loanTerm",
            isFullWidth: true,
            size: "md",
            type: "text",
            id: generateId('current-loan-term'),
            onChange: function onChange() {
              return undefined;
            },
            onFocus: loanTermHandler.focus,
            onBlur: loanTermHandler.blur,
            after: /*#__PURE__*/jsx(InputAfter, {
              children: "years"
            })
          }), errors.loanTerm && /*#__PURE__*/jsxs(Body, {
            color: "error.500",
            size: "xs",
            children: [/*#__PURE__*/jsx(Error, {
              color: "error.500"
            }), " ", errors.loanTerm.message]
          })]
        }), /*#__PURE__*/jsxs(StyledUnit, {
          size: {
            lg: 1 / 4,
            sm: 1 / 2,
            xs: 1
          },
          children: [/*#__PURE__*/jsx("label", {
            htmlFor: generateId('initial-offset-balance'),
            children: /*#__PURE__*/jsx(StyledHeading$1, {
              size: "xxs",
              children: "Initial offset balance"
            })
          }), /*#__PURE__*/jsx(Input, {
            ref: register,
            name: "initialOffsetBalance",
            isFullWidth: true,
            size: "md",
            type: "text",
            id: generateId('initial-offset-balance'),
            onChange: function onChange() {
              return undefined;
            },
            onFocus: initialOffsetBalanceHandler.focus,
            onBlur: initialOffsetBalanceHandler.blur,
            before: /*#__PURE__*/jsx(InputBefore, {
              children: "$"
            })
          }), errors.initialOffsetBalance && /*#__PURE__*/jsxs(Body, {
            color: "error.500",
            size: "xs",
            children: [/*#__PURE__*/jsx(Error, {
              color: "error.500"
            }), " ", errors.initialOffsetBalance.message]
          })]
        }), /*#__PURE__*/jsxs(StyledUnit, {
          size: {
            lg: 1 / 4,
            sm: 1 / 2,
            xs: 1
          },
          children: [/*#__PURE__*/jsxs(StyledHeading$1, {
            size: "xxs",
            children: [/*#__PURE__*/jsx("label", {
              htmlFor: generateId('monthly-offset-increment'),
              children: "Contributions to offset"
            }), /*#__PURE__*/jsx(StyledLendiTooltip, {
              content: contributionsTooltipContent,
              position: "right",
              children: /*#__PURE__*/jsx(Help, {
                height: "20px",
                width: "20px",
                color: "primary.600"
              })
            })]
          }), /*#__PURE__*/jsx(Input, {
            ref: register,
            name: "monthlyOffsetIncrement",
            isFullWidth: true,
            size: "md",
            type: "text",
            id: generateId('monthly-offset-increment'),
            onChange: function onChange() {
              return undefined;
            },
            onFocus: monthlyOffsetIncrementHandler.focus,
            onBlur: monthlyOffsetIncrementHandler.blur,
            before: /*#__PURE__*/jsx(InputBefore, {
              children: "$"
            }),
            after: /*#__PURE__*/jsx(InputAfter, {
              children: "/ month"
            })
          }), errors.monthlyOffsetIncrement && /*#__PURE__*/jsxs(Body, {
            color: "error.500",
            size: "xs",
            children: [/*#__PURE__*/jsx(Error, {
              color: "error.500"
            }), " ", errors.monthlyOffsetIncrement.message]
          })]
        }), /*#__PURE__*/jsxs(StyledUnit, {
          size: {
            lg: 1 / 4,
            sm: 1 / 2,
            xs: 1
          },
          children: [/*#__PURE__*/jsxs(StyledHeading$1, {
            size: "xxs",
            children: [/*#__PURE__*/jsx("label", {
              htmlFor: generateId('offset-start-delay'),
              children: "Start offset after"
            }), /*#__PURE__*/jsx(StyledLendiTooltip, {
              content: offsetDelayTooltipContent,
              position: "right",
              children: /*#__PURE__*/jsx(Help, {
                height: "20px",
                width: "20px",
                color: "primary.600"
              })
            })]
          }), /*#__PURE__*/jsx(Input, {
            ref: register,
            name: "offsetStartDelay",
            isFullWidth: true,
            size: "md",
            type: "text",
            id: generateId('offset-start-delay'),
            onChange: function onChange() {
              return undefined;
            },
            onFocus: offsetStartDelayHandler.focus,
            onBlur: offsetStartDelayHandler.blur,
            after: /*#__PURE__*/jsx(InputAfter, {
              children: "years"
            })
          }), errors.offsetStartDelay && /*#__PURE__*/jsxs(Body, {
            color: "error.500",
            size: "xs",
            children: [/*#__PURE__*/jsx(Error, {
              color: "error.500"
            }), " ", errors.offsetStartDelay.message]
          })]
        }), /*#__PURE__*/jsx(StyledUnit, {
          size: 1,
          children: /*#__PURE__*/jsx(StyledButton, {
            "data-testid": "calculate-button",
            type: "submit",
            variant: "primary",
            size: "md"
            // Form needs to be valid AND values need to be changed from last submit
            ,
            isDisabled: !(isValid && (isDirty || !isSubmitted)),
            children: "Calculate"
          })
        })]
      })]
    }), results && /*#__PURE__*/jsx(ResultsPanel, _objectSpread(_objectSpread({}, results), {}, {
      buttonLink: buttonLink,
      buttonText: buttonText,
      resultBody: resultBody,
      resultHeading: resultHeading
    })), results && /*#__PURE__*/jsxs(ResultsWrapper, {
      children: [showGraph && graphHeading && /*#__PURE__*/jsx(GraphHeading, {
        size: "sm",
        align: "center",
        children: graphHeading
      }), chartData && showGraph && /*#__PURE__*/jsx(Chart, {
        chartData: chartData,
        xAxis: {
          key: 'years',
          value: results.loanTerm,
          label: 'Loan term (years)'
        },
        yAxis: {
          key: 'withOffset',
          value: results.loanAmount,
          label: "Loan Balance ('000s)"
        },
        lines: [{
          color: Colors.WithoutOffset,
          dataKey: 'withoutOffset'
        }, {
          color: Colors.WithOffset,
          dataKey: 'withOffset'
        }],
        tooltip: /*#__PURE__*/jsx(Tooltip, {
          cursor: false,
          content: /*#__PURE__*/jsx(GraphTooltip, {})
        })
      }), showGraph && /*#__PURE__*/jsx(PaymentSummary, {
        children: /*#__PURE__*/jsxs(Grid, {
          children: [/*#__PURE__*/jsx(Grid.Unit, {
            size: 1 / 2,
            children: "Current loan term"
          }), /*#__PURE__*/jsxs(RightAlignUnit, {
            size: 1 / 2,
            children: [results.loanTerm, " years"]
          }), /*#__PURE__*/jsx(Seperator$1, {}), /*#__PURE__*/jsx(Grid.Unit, {
            size: 1 / 2,
            children: "New loan term"
          }), /*#__PURE__*/jsx(RightAlignUnit, {
            size: 1 / 2,
            children: parseTime(results.newLoanTerm)
          })]
        })
      }), disclaimer && /*#__PURE__*/jsx(Summary, {
        children: disclaimer
      })]
    })]
  });
};

export { RepaymentsCalculator as default, parseTime };
